import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Plus, Edit3, Trash2, X, Star, ToggleLeft, ToggleRight, Inbox, CheckCircle, List, Clock } from 'lucide-react';
import { testimonialsApi } from '../../api/testimonials';

const extractRating = (item) => {
  if (!item) return 5;
  const val = item.rating ?? item.Rating ?? item.stars ?? item.Stars ?? item.rate ?? item.Rate ?? item.score ?? 5;
  return Math.round(Number(val)) || 5;
};

const StarRating = ({ rating }) => (
  <div style={{ display: 'flex', gap: 4, direction: 'ltr' }}>
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} size={14} fill={n <= rating ? '#facc15' : 'none'} stroke={n <= rating ? '#eab308' : '#cbd5e1'} />
    ))}
  </div>
);

export function TestimonialsManagement() {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); 
  const [form, setForm] = useState({ clientName: '', clientRole: '', institutionName: '', message: '', rating: 5, isActive: false });

  const [viewedIds, setViewedIds] = useState(() => {
    return JSON.parse(localStorage.getItem('ayonk_viewed_testimonials') || '[]');
  });

  useEffect(() => { fetchAll(true); }, []);
  useEffect(() => {
    localStorage.setItem('ayonk_viewed_testimonials', JSON.stringify(viewedIds));
  }, [viewedIds]);

  const fetchAll = async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const data = await testimonialsApi.getAll();
      setTestimonials(Array.isArray(data) ? data.sort((a,b) => b.id - a.id) : []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selected) {
        await testimonialsApi.update(selected.id, { ...form, id: selected.id });
        if (!viewedIds.includes(selected.id)) setViewedIds([...viewedIds, selected.id]);
      } else {
        await testimonialsApi.create(form);
      }
      await fetchAll();
      setIsModalOpen(false);
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const openEdit = (t) => {
    setSelected(t);
    setForm({
        clientName: t.clientName || '',
        clientRole: t.clientRole || '',
        institutionName: t.institutionName || '',
        message: t.message || '',
        rating: extractRating(t),
        isActive: t.isActive
    });
    if (!viewedIds.includes(t.id)) setViewedIds([...viewedIds, t.id]);
    setIsModalOpen(true);
  };

  const toggleActive = async (t) => {
    await testimonialsApi.update(t.id, {...t, isActive: !t.isActive});
    if (!viewedIds.includes(t.id)) setViewedIds([...viewedIds, t.id]);
    await fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRtl ? 'حذف؟' : 'Delete?')) return;
    await testimonialsApi.delete(id);
    fetchAll();
  };

  const published = testimonials.filter(t => t.isActive);
  const unpublished = testimonials.filter(t => !t.isActive);
  const newRequests = unpublished.filter(t => !viewedIds.includes(t.id));
  const pendingRequests = unpublished.filter(t => viewedIds.includes(t.id));

  const displayData = 
    activeTab === 'all' ? testimonials : 
    activeTab === 'published' ? published :
    activeTab === 'new' ? newRequests : pendingRequests;

  const tabStyle = (id, color) => ({
    padding: '10px 18px', cursor: 'pointer', background: activeTab === id ? '#fff' : 'transparent',
    border: 'none', borderBottom: activeTab === id ? `3px solid ${color}` : '3px solid transparent',
    color: activeTab === id ? '#000' : '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, transition: '0.3s'
  });

  const Badge = ({ count, color }) => count > 0 ? (
    <span style={{ background: color, color: 'white', fontSize: 10, padding: '2px 7px', borderRadius: 10, marginLeft: 4 }}>{count}</span>
  ) : null;

  return (
    <div className="admin-page-content" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="admin-page-header">
        <h1>{isRtl ? 'مركز إدارة الآراء' : 'Reviews Center'}</h1>
        <button className="btn-add-primary" onClick={()=>{setSelected(null); setForm({clientName:'', clientRole:'', institutionName:'', message:'', rating:5, isActive:false}); setIsModalOpen(true);}}>
          <Plus size={18}/><span>{isRtl?'إضافة':'Add'}</span>
        </button>
      </header>

      <div style={{display:'flex', background:'#f1f5f9', borderRadius:10, padding:4, marginBottom:20, width:'fit-content', gap:4}}>
        <button style={tabStyle('all', '#64748b')} onClick={()=>setActiveTab('all')}><List size={18}/> {isRtl?'الكل':'All'}</button>
        <button style={tabStyle('published', '#10b981')} onClick={()=>setActiveTab('published')}>
          <CheckCircle size={18} color="#10b981"/> {isRtl?'المنشورة':'Live'} <Badge count={published.length} color="#10b981"/>
        </button>
        <button style={tabStyle('new', '#ef4444')} onClick={()=>setActiveTab('new')}>
          <Inbox size={18} color="#ef4444"/> {isRtl?'الجديدة':'New'} <Badge count={newRequests.length} color="#ef4444"/>
        </button>
        <button style={tabStyle('pending', '#f59e0b')} onClick={()=>setActiveTab('pending')}>
          <Clock size={18} color="#f59e0b"/> {isRtl?'الانتظار':'Pending'} <Badge count={pendingRequests.length} color="#f59e0b"/>
        </button>
      </div>

      <div className="admin-content-card">
        <table className="admin-table">
          <thead>
            <tr><th>#</th><th>{isRtl?'العميل':'Client'}</th><th>{isRtl?'الجهة':'Institution'}</th><th>{isRtl?'التقييم':'Rating'}</th><th>{isRtl?'الحالة':'Status'}</th><th>{isRtl?'إجراءات':'Actions'}</th></tr>
          </thead>
          <tbody>
            {displayData.map((t, idx) => (
              <tr key={t.id} style={{ borderRight: !t.isActive && !viewedIds.includes(t.id) ? '4px solid #ef4444' : 'none' }}>
                <td>{idx+1}</td>
                <td>
                  <div style={{fontWeight:700}}>{t.clientName}</div>
                  <div style={{fontSize:11, color:'#94a3b8', fontWeight: 500}}>{t.clientRole}</div>
                </td>
                <td style={{fontSize:13, color:'#64748b'}}>{t.institutionName}</td>
                <td><StarRating rating={extractRating(t)} /></td>
                <td>
                  <button onClick={()=>toggleActive(t)} style={{background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8}}>
                    {t.isActive ? <><ToggleRight size={22} color="#10b981"/><span style={{color:'#10b981', fontWeight:700, fontSize:11}}>{isRtl?'نشط':'Live'}</span></>
                                : <><ToggleLeft size={22} color="#64748b"/><span style={{color:'#64748b', fontWeight:700, fontSize:11}}>{isRtl?'مخفي':'Hidden'}</span></>}
                  </button>
                </td>
                <td className="actions-cell">
                  <button className="btn-icon edit" onClick={()=>openEdit(t)}><Edit3 size={16}/></button>
                  <button className="btn-icon delete" onClick={()=>handleDelete(t.id)}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>{selected ? (isRtl ? 'تعديل' : 'Edit') : (isRtl ? 'إضافة' : 'Add')}</h2>
              <button className="modal-close" onClick={()=>setIsModalOpen(false)}><X size={24}/></button>
            </div>
            <form className="admin-form" onSubmit={handleSave}>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                <div className="form-field"><label>{isRtl?'اسم العميل':'Name'}</label><input value={form.clientName} onChange={e=>setForm({...form, clientName:e.target.value})} /></div>
                <div className="form-field"><label>{isRtl?'الوظيفة':'Role'}</label><input value={form.clientRole} onChange={e=>setForm({...form, clientRole:e.target.value})} /></div>
                <div className="form-field" style={{gridColumn:'1/-1'}}><label>{isRtl?'الجهة / الشركة':'Company'}</label><input value={form.institutionName} onChange={e=>setForm({...form, institutionName:e.target.value})} /></div>
                <div className="form-field" style={{gridColumn:'1/-1'}}><label>{isRtl?'الرسالة':'Message'}</label><textarea rows="3" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} /></div>
              </div>
              <div className="modal-footer"><button type="submit" className="btn-add-primary">{isRtl?'حفظ':'Save'}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
